import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight,
  ArrowUpRight,
  Music2,
  ShieldCheck,
  Trophy,
  Sparkles,
  MapPin,
  ChevronDown,
} from "lucide-react";
import Marquee from "@/components/Marquee";
import { FadeUp, MaskedLines, Overline } from "@/components/Reveal";

const IMG = {
  hero: "/assets/cetate-drona-apus.jpg",
  wallie: "/assets/wallie-padure-iarna.jpg",
};

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "24%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section ref={ref} className="relative flex min-h-[100svh] items-end overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
        <img
          src={IMG.hero}
          alt="Cetatea Alba Carolina din Alba Iulia, vedere aeriană"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-canvas/70" />
        <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-canvas via-canvas/40 to-transparent" />
      </motion.div>

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-24 pt-40 sm:px-8 lg:px-12"
      >
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="mb-6 font-mono text-xs font-bold uppercase tracking-[0.3em] text-neon"
        >
          Asociația Art & Outdoor România — ONG
        </motion.p>

        <h1
          data-testid="hero-title"
          className="max-w-5xl font-display text-4xl font-black uppercase leading-[0.98] tracking-tight text-white sm:text-6xl lg:text-7xl"
        >
          <MaskedLines
            lines={[
              <>Cultură.</>,
              <>Outdoor.</>,
              <span key="l3" className="text-neon">Energie pură.</span>,
            ]}
          />
        </h1>

        <FadeUp delay={0.65}>
          <p
            data-testid="hero-subtitle"
            className="mt-8 max-w-xl text-base leading-relaxed text-neutral-300 sm:text-lg"
          >
            Prevenim bullying-ul și discriminarea între copii prin cultură,
            sport și natură. Fiecare festival, turneu sau atelier al nostru duce
            același mesaj: respect, curaj și fair-play.
          </p>
        </FadeUp>

        <FadeUp delay={0.8} className="mt-10 flex flex-wrap items-center gap-4">
          <Link
            to="/bushido-vs-bullying"
            data-testid="hero-primary-cta"
            className="group flex items-center gap-2 rounded-full bg-neon px-8 py-4 font-display text-sm font-bold uppercase tracking-wider text-canvas transition-colors duration-300 hover:bg-white"
          >
            Descoperă proiectul
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <Link
            to="/contact"
            data-testid="hero-secondary-cta"
            className="flex items-center gap-2 rounded-full border border-white/25 px-8 py-4 font-display text-sm font-bold uppercase tracking-wider text-white transition-colors duration-300 hover:border-neon hover:text-neon"
          >
            Implică-te
          </Link>
        </FadeUp>

        <FadeUp delay={0.95} className="mt-12 flex flex-wrap gap-3">
          <span className="flex items-center gap-2 rounded-full border border-neon/40 bg-canvas/60 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-neon backdrop-blur-sm">
            <span className="h-1.5 w-1.5 animate-pulse-glow rounded-full bg-neon" />
            Bushido vs. Bullying — proiect principal
          </span>
          <span className="flex items-center gap-2 rounded-full border border-white/15 bg-canvas/60 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-400 backdrop-blur-sm">
            <MapPin className="h-3 w-3" />
            Alba Iulia · Cluj-Napoca · România
          </span>
        </FadeUp>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-8 right-8 z-10 hidden text-neutral-500 lg:block"
      >
        <ChevronDown className="h-6 w-6 animate-bounce" />
      </motion.div>
    </section>
  );
};

const BUSHIDO_PILLARS = [
  {
    icon: ShieldCheck,
    id: "bushido-pillar-respect",
    title: "Respect & Curaj",
    text: "Cele șase valori ale codului Bushido — respect, curaj, integritate, compasiune, disciplină, onoare — traduse pe înțelesul copiilor.",
  },
  {
    icon: Music2,
    id: "bushido-pillar-cultura",
    title: "Cultură & Istorie",
    text: "Muzica pe care o ascultă tinerii și istoria orașului devin limbajul prin care vorbim despre bullying: cine ești, de unde vii, cum îți aperi demnitatea fără să rănești.",
  },
  {
    icon: Trophy,
    id: "bushido-pillar-sport",
    title: "Sport & Fair-Play",
    text: "Pe teren se învață cel mai repede: respectul pentru adversar, disciplina echipei și curajul de a apăra coechipierul — nu de a-l umili.",
  },
  {
    icon: Sparkles,
    id: "bushido-pillar-natura",
    title: "Natură & Ateliere",
    text: "Experiențe în aer liber și ateliere creative unde copiii se descoperă fără competiție toxică și fără etichete.",
  },
];

const BushidoSection = () => (
  <section data-testid="bushido-home-section" className="relative overflow-hidden">
    <span
      aria-hidden="true"
      className="pointer-events-none absolute -right-16 top-4 select-none font-display text-[18rem] font-black leading-none text-white/[0.03] sm:text-[26rem]"
    >
      武
    </span>
    <div className="relative mx-auto max-w-7xl px-4 py-24 sm:px-8 lg:px-12 lg:py-36">
      <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
        <div className="lg:col-span-5">
          <FadeUp>
            <div className="mb-6 inline-flex rounded-full bg-flame px-4 py-2 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-white">
              Proiectul Principal
            </div>
            <h2 className="font-display text-3xl font-black uppercase leading-[1.02] tracking-tight text-white sm:text-4xl lg:text-5xl">
              Bushido vs. <span className="text-neon">Bullying</span>
            </h2>
            <p className="mt-6 text-base leading-relaxed text-neutral-300 sm:text-lg">
              O metodă nouă de prevenire a bullying-ului și a discriminării între
              copii și în școli, inspirată din calea războinicilor samurai: nu
              despre luptă, ci despre stăpânire de sine.
            </p>
            <p className="mt-5 text-sm leading-relaxed text-neutral-400">
              Toate evenimentele noastre — festivalul, turneul de fotbal,
              atelierele — sunt proiecte în cadrul acestui scop. Cultura și
              istoria le arată tinerilor de unde vine demnitatea, iar sportul îi
              învață fair-play-ul și respectul pentru celălalt.
            </p>
          </FadeUp>
          <FadeUp delay={0.2}>
            <Link
              to="/bushido-vs-bullying"
              data-testid="bushido-home-cta"
              className="group mt-10 inline-flex items-center gap-2 rounded-full bg-neon px-8 py-4 font-display text-sm font-bold uppercase tracking-wider text-canvas transition-colors duration-300 hover:bg-white"
            >
              Vezi proiectul complet
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </FadeUp>
        </div>

        <div className="lg:col-span-7">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            {BUSHIDO_PILLARS.map(({ icon: Icon, id, title, text }, i) => (
              <FadeUp key={id} delay={i * 0.08}>
                <div
                  data-testid={id}
                  className={`group h-full rounded-3xl border border-white/10 bg-surface p-7 transition-colors duration-500 hover:border-neon/50 ${
                    i % 2 === 1 ? "sm:mt-8" : ""
                  }`}
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-carddark text-neon transition-transform duration-500 group-hover:-rotate-6">
                    <Icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-5 font-display text-base font-black uppercase tracking-wide text-white">
                    {title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-neutral-400">{text}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

const EVENTS = [
  {
    id: "home-event-rise27",
    to: "/rise27",
    tag: "Festival · Vara 2027",
    title: "RISE 27'",
    text: "Concerte cu muzica ascultată de tineri, la Cetatea Alba Carolina, cu mesaj de respect și incluziune.",
  },
  {
    id: "home-event-cupa",
    to: "/cupa-caritabila",
    tag: "Sport caritabil · 2026",
    title: "Cupa de fotbal",
    text: "Mini-campionat în Alba Iulia, echipe 8+1, fair-play pe teren și fonduri pentru proiectele copiilor.",
  },
  {
    id: "home-event-wallie",
    to: "/proiecte",
    tag: "Finalizat · 2018–2020",
    title: "Wallie Forest Kidz",
    text: "Grădinița în natură din Cluj-Napoca: învățare prin explorare, joc liber și respect pentru ceilalți.",
  },
];

const EventsStrip = () => (
  <section data-testid="home-events-strip" className="border-y border-white/10 bg-surface">
    <div className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:px-12 lg:py-24">
      <FadeUp>
        <Overline>Evenimente în cadrul proiectului</Overline>
        <h2 className="mt-4 max-w-2xl font-display text-2xl font-black uppercase leading-[1.05] tracking-tight text-white sm:text-3xl lg:text-4xl">
          Același mesaj, <span className="text-neon">trei experiențe</span>
        </h2>
      </FadeUp>
      <div className="mt-12 grid grid-cols-1 gap-px bg-white/10 md:grid-cols-3">
        {EVENTS.map((e, i) => (
          <FadeUp key={e.id} delay={i * 0.08} className="bg-surface">
            <Link
              to={e.to}
              data-testid={e.id}
              className="group block h-full p-7 transition-colors duration-500 hover:bg-carddark"
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-flame">{e.tag}</p>
              <h3 className="mt-4 flex items-center gap-2 font-display text-xl font-black uppercase tracking-tight text-white transition-colors duration-300 group-hover:text-neon">
                {e.title}
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-neutral-400">{e.text}</p>
            </Link>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

const CHAPTERS = [
  {
    id: "manifesto-chapter-1",
    nr: "01",
    title: "Respect, nu frică",
    text: "Bullying-ul se destramă acolo unde respectul devine normă. Învățăm copiii să își apere demnitatea fără să rănească pe nimeni.",
  },
  {
    id: "manifesto-chapter-2",
    nr: "02",
    title: "Cultura ca limbaj",
    text: "Muzica, istoria și arta ajung la tineri mai repede decât orice lecție. Le folosim ca să vorbim despre identitate, incluziune și curaj.",
  },
  {
    id: "manifesto-chapter-3",
    nr: "03",
    title: "Sportul ca școală",
    text: "Fair-play, disciplină, sprijin pentru coechipier: pe teren se învață cel mai repede ce înseamnă să fii puternic fără să umilești.",
  },
  {
    id: "manifesto-chapter-4",
    nr: "04",
    title: "Natura ca resetare",
    text: "În aer liber dispar ierarhiile și etichetele. Copiii se descoperă prin joc liber, explorare și tăcere.",
  },
];

const Manifesto = () => (
  <section className="border-t border-white/10 bg-surface">
    <div className="mx-auto max-w-7xl px-4 py-24 sm:px-8 lg:px-12 lg:py-36">
      <FadeUp>
        <Overline>Manifestul nostru</Overline>
      </FadeUp>
      <div className="mt-12">
        {CHAPTERS.map((c, i) => (
          <FadeUp key={c.id} delay={i * 0.08}>
            <div
              data-testid={c.id}
              className="group grid grid-cols-1 gap-4 border-t border-white/10 py-10 transition-colors duration-500 last:border-b hover:bg-canvas/40 md:grid-cols-12 md:items-center md:gap-8"
            >
              <span className="font-mono text-sm font-bold text-flame md:col-span-2">{c.nr} /</span>
              <h3 className="font-display text-2xl font-black uppercase tracking-tight text-white transition-colors duration-300 group-hover:text-neon sm:text-3xl md:col-span-4">
                {c.title}
              </h3>
              <p className="text-base leading-relaxed text-neutral-400 md:col-span-6">{c.text}</p>
            </div>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

const WallieSection = () => (
  <section data-testid="wallie-section" className="mx-auto max-w-7xl px-4 py-24 sm:px-8 lg:px-12 lg:py-36">
    <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-16">
      <div className="order-2 lg:order-1 lg:col-span-5">
        <FadeUp>
          <span className="rounded-full bg-flame/15 px-4 py-2 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-flame">
            Finalizat · 2018–2020
          </span>
          <h2 className="mt-6 font-display text-3xl font-black uppercase leading-[1.02] tracking-tight text-white sm:text-4xl">
            Wallie Forest Kidz
          </h2>
          <p className="mt-6 text-base leading-relaxed text-neutral-300 sm:text-lg">
            Grădinița din pădure a Clujului. Între 2018 și 2020, copiii au învățat
            prin explorare directă în natură, joc liber și stimulare senzorială —
            primul nostru proiect de educație outdoor imersivă.
          </p>
          <p className="mt-4 text-sm leading-relaxed text-neutral-400">
            Proiect finalizat, moștenire vie: tot ce am învățat acolo a devenit
            fundamentul pentru RISE 27'.
          </p>
          <Link
            to="/proiecte"
            data-testid="wallie-view-details-btn"
            className="group mt-8 inline-flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-neon transition-colors hover:text-white"
          >
            Vezi Detalii
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </FadeUp>
      </div>
      <FadeUp className="order-1 lg:order-2 lg:col-span-7" delay={0.1}>
        <motion.div
          whileHover={{ scale: 0.985 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="overflow-hidden rounded-3xl border border-white/10"
        >
          <img src={IMG.wallie} alt="Copii explorând pădurea la Wallie Forest Kidz" className="aspect-[4/3] w-full object-cover" />
        </motion.div>
      </FadeUp>
    </div>
  </section>
);

const STATS = [
  { value: "15.000+", label: "Tineri Implicați" },
  { value: "2018 →", label: "Ani de Activitate" },
  { value: "120+", label: "Ateliere Outdoor" },
  { value: "2", label: "Orașe — Cluj & Alba Iulia" },
];

const Stats = () => (
  <section className="border-t border-white/10">
    <div
      data-testid="impact-stats-grid"
      className="mx-auto grid max-w-7xl grid-cols-2 gap-px bg-white/10 px-0 lg:grid-cols-4"
    >
      {STATS.map((s, i) => (
        <FadeUp key={s.label} delay={i * 0.08} className="bg-canvas">
          <div className="px-6 py-12 text-center lg:py-16">
            <span className="block font-display text-3xl font-black text-neon sm:text-4xl">{s.value}</span>
            <span className="mt-3 block font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-500">
              {s.label}
            </span>
          </div>
        </FadeUp>
      ))}
    </div>
  </section>
);

const CtaBand = () => (
  <section className="mx-auto max-w-7xl px-4 py-24 sm:px-8 lg:px-12 lg:py-36">
    <FadeUp className="rounded-3xl border border-neon/25 bg-surface px-6 py-16 text-center sm:px-12 lg:py-24">
      <Overline className="text-center">Alătură-te mișcării</Overline>
      <h2 className="mx-auto mt-6 max-w-3xl font-display text-3xl font-black uppercase leading-[1.05] tracking-tight text-white sm:text-4xl lg:text-5xl">
        Hai să scriem <span className="text-neon">următorul capitol</span> împreună
      </h2>
      <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-neutral-400">
        Voluntariat, parteneriate sau idei noi — fiecare mână contează înainte de RISE 27'.
      </p>
      <Link
        to="/contact"
        data-testid="home-contact-cta"
        className="group mt-10 inline-flex items-center gap-2 rounded-full bg-neon px-10 py-4 font-display text-sm font-bold uppercase tracking-wider text-canvas transition-colors duration-300 hover:bg-white"
      >
        Contactează-ne
        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
      </Link>
    </FadeUp>
  </section>
);

export default function Home() {
  return (
    <>
      <Hero />
      <Marquee />
      <BushidoSection />
      <EventsStrip />
      <Manifesto />
      <WallieSection />
      <Stats />
      <CtaBand />
    </>
  );
}
